import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  Injector,
  input,
  OnInit,
  output,
  Signal,
  signal,
  untracked,
  viewChild,
  viewChildren,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  isPinnedFilter,
  SimpleFilterChangeEvent,
  SimpleFilterConfiguration,
  SimpleFilterType,
} from '../../data/interfaces'
import { SimpleFilterComponent } from '../simple-filter/simple-filter.component'
import { DecimalPipe } from '@angular/common'
import { FiltersCatalogService } from '../../data/filters-catalog.service'
import { ResponsiveFilterService } from '../../helpers/responsive-filter.service'
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  Observable,
  Subject,
  tap,
} from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { SimpleFilterExternalState } from '../../data/simple-filter-external.state'
import { NgClickOutsideDirective } from 'ng-click-outside2'
import { SizeDirective } from '@wm-front/shared/common-ui'
import { ButtonComponent, IconButtonComponent } from '@wm-front/shared/buttons'
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FilterValues } from '@wm-front/shared/routed-filters-service'
import { NewThemeService } from '@wm-front/core-tools'
import { MatIconModule } from '@angular/material/icon'

@UntilDestroy()
@Component({
  selector: 'wm-front-simple-filters',
  imports: [
    SimpleFilterComponent,
    SizeDirective,
    ButtonComponent,
    DecimalPipe,
    IconButtonComponent,
    NgClickOutsideDirective,
    MatIconModule,
  ],
  templateUrl: './simple-filters.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./simple-filters.component.scss'],
})
export class SimpleFiltersComponent implements OnInit, AfterViewInit {
  /** Will tell you if the display should be minimized, ie only a button */
  public minimizedMode = input<boolean>()

  public expendMode = input<boolean>()

  /** The filters to calculate from */
  public filters = viewChildren(SimpleFilterComponent<any>, {
    read: ElementRef,
  })

  /** The responsive menu */
  public responsiveMenu = viewChild('responsiveMenu', {
    read: ElementRef,
  })

  /** The filters to calculate from */
  public responsiveToggler = viewChild('responsiveToggler', {
    read: ElementRef,
  })

  /** The configurations of the filters */
  public configurations = input.required<SimpleFilterConfiguration<any>[]>()

  /** Will be triggered on filter changed */
  public filterChanged = output<Partial<FilterValues>>()

  /** The trigger to reset the filters */
  public resetFilter$ = input(new Subject<void>())

  /** The configuration converted to something understandable by the component */
  public currentConfigurations = computed(() =>
    this.configurations().map((configuration) => ({
      ...this._catalog.getConfiguration(configuration),
      pinned: isPinnedFilter(configuration),
    }))
  )

  /** The configurations managed by the filters it self */
  public internalConfigurations: Signal<any> = computed(() =>
    this.currentConfigurations()
      .filter(
        (configuration) => configuration?.type !== SimpleFilterType.EXTERNAL
      )
      .map((configuration, index) => ({
        ...configuration,
        width: this.filtersInitialWidths()[index],
      }))
  )

  /** The configurations managed by some other systems */
  public externalConfigurations = computed(() =>
    this.currentConfigurations().filter(
      (configuration) => configuration?.type === SimpleFilterType.EXTERNAL
    )
  )

  /** 2026 theme (PRENG-8240): pinned filters stay in the bar, the others go in the Filtres button, whatever the width */
  public pinnedLayout = computed(
    () =>
      this._newTheme.enabled() && !this.minimizedMode() && !this.expendMode()
  )

  /** Only the filters we want to keep on display */
  public insideFiltersConfigurations = computed(() =>
    this.pinnedLayout()
      ? this.internalConfigurations().filter(
          (configuration: SimpleFilterConfiguration<any>) =>
            configuration.pinned
        )
      : this.internalConfigurations().slice(0, this.breakingPoint())
  )

  /** Only the filters we want to keep hidden in the filters button */
  public outsideFiltersConfigurations = computed(() =>
    this.pinnedLayout()
      ? this.internalConfigurations().filter(
          (configuration: SimpleFilterConfiguration<any>) =>
            !configuration.pinned
        )
      : this.internalConfigurations().slice(this.breakingPoint())
  )

  /** The values of the different filters */
  public values = input<{ [key: string]: string }>()

  /** Will hold if we want the responsive to be displayed ot not */
  public displayResponsiveFilters = signal(false)

  /** Will hold the number of responsive filter which have value */
  public numberOfResponsiveFilterWhichHaveValue = computed(() => {
    return Object.keys(this.currentFilterValues()).filter((key) =>
      this.outsideFiltersConfigurations().some((configuration) => {
        const keyToUse = configuration.key || configuration.dataType
        const valueToUse = this.currentFilterValues()[keyToUse as string]
        return (
          keyToUse &&
          keyToUse === key &&
          valueToUse !== undefined &&
          (valueToUse?.length !== undefined ? valueToUse.length > 0 : true)
        )
      })
    ).length
  })

  /** Will tell if the responsive should be aligned right */
  public responsiveFilterShouldAlignToTheRight = computed(() => {
    if (this.containerWidth() > 0 && !this.displayResponsiveFilters()) {
      return false
    }
    const boundingRight =
      this.responsiveMenu()?.nativeElement.getBoundingClientRect().right
    const boundingLeft =
      this.responsiveMenu()?.nativeElement.getBoundingClientRect().left
    const documentWidth = document.body.scrollWidth
    const minMarginRight = this.filterDropdownWidth()
    if (boundingLeft < minMarginRight) {
      return false
    }
    return (
      boundingRight > this.filterDropdownWidth() &&
      documentWidth - boundingRight < minMarginRight
    )
  })

  /** The filter dropdown width: the `size-filter-dropdown` token (380px, 300px in the 2026 theme) */
  public readonly filterDropdownWidth = computed(() => {
    this._newTheme.enabled()

    return this._newTheme.readPx('size-filter-dropdown') ?? 380
  })

  /** Will tell you which filter is open */
  public responsiveFilterOpened = signal('')

  public emitOnInit = input(false)

  private _catalog = inject(FiltersCatalogService)

  private _newTheme = inject(NewThemeService)

  private _responsiveFilterService = inject(ResponsiveFilterService)

  private _injector = inject(Injector)

  private _externalState = inject(SimpleFilterExternalState)

  /** The index from which we need to put stuff in the responsive part of the display */
  private breakingPoint = signal(100)

  /** The initial width of the filters */
  private filtersInitialWidths = signal<number[]>([])

  /** The current gap */
  private readonly gapForResponsive = 16

  /** Hold the current value */
  private currentFilterValues = signal<Partial<FilterValues>>({})

  /** Will be triggered on size changed */
  private sizeChanged = new Subject<number>()

  /** The container width */
  private containerWidth = signal<number>(1000)

  /** Seems to be a nice debounce time */
  private debounceTime = 100

  public ngOnInit(): void {
    this.manageSize()
    this.setCurrentValues()
    this.manageExternalConfigurations()
  }

  public ngAfterViewInit(): void {
    this.setFiltersInitialWidths()
  }

  /** What will happen when the filter change */
  public onFilterChanged(event: SimpleFilterChangeEvent): void {
    const result = {
      ...this.currentFilterValues(),
      [event.key]: event.value,
    }
    this.currentFilterValues.set(result as any)
    this.filterChanged.emit(this.currentFilterValues())
  }

  /**
   * Will be triggered when the container size will change
   * @param width
   */
  public onSizeChanged(width: number) {
    this.sizeChanged.next(width)
  }

  /**
   * What will happen when the user reset specifically the responsive filter
   */
  public resetResponsiveFilters(): void {
    this.resetFilter$().next()
  }

  /**
   * Will know what to do when a filter is open or closed
   * @param configuration
   * @param isOpen
   */
  public manageOpenChange(
    configuration: SimpleFilterConfiguration<any>,
    isOpen: boolean
  ): void {
    const keyToUse = configuration.key || configuration.dataType
    this.responsiveFilterOpened.set(isOpen && keyToUse ? keyToUse : '')
  }

  /**
   * Will set the initial width for the filters
   * @private
   */
  private setFiltersInitialWidths(): void {
    this.filtersInitialWidths.set(
      this.filters().map(
        (filter) => filter.nativeElement.offsetWidth + this.gapForResponsive
      )
    )
  }

  /** What will happen when multiple filters change */
  private onFiltersChanged(events: SimpleFilterChangeEvent[]): void {
    const filtersAsValues = events.reduce(
      (previous, next) => ({ ...previous, [next.key]: next.value }),
      {}
    )
    this._externalState.patchState(filtersAsValues)
    const result = { ...this.currentFilterValues(), ...filtersAsValues }
    this.currentFilterValues.set(result)
    this.filterChanged.emit(this.currentFilterValues())
  }

  /**
   * What should happen on size changed ?
   * @private
   */
  private manageSize(): void {
    this.sizeChanged
      .pipe(
        debounceTime(this.debounceTime),
        distinctUntilChanged(),
        tap((width) => this.recomputeResponsive(width)),
        tap((width) => this.containerWidth.set(width)),
        untilDestroyed(this)
      )
      .subscribe()
  }

  /**
   * The recomputation of the responsive
   * @param width
   * @private
   */
  private recomputeResponsive(width: number): void {
    if (!this.minimizedMode()) {
      const result =
        this._responsiveFilterService.getInsideAndOutsideElementsFromAContainerWidth(
          this.internalConfigurations(),
          width,
          this.responsiveToggler()?.nativeElement.offsetWidth || 0
        )
      this.breakingPoint.set(result.insideElements?.length)
    } else {
      this.breakingPoint.set(0)
    }
  }

  /**
   * Will set the current values from the values if it exists
   * @private
   */
  private setCurrentValues(): void {
    if (Object.keys(this.values() || {})?.length) {
      this.currentFilterValues.set(this.values() || {})
    }
  }

  /**
   * The external configuration management
   * @private
   */
  private manageExternalConfigurations(): void {
    effect(
      () => {
        combineLatest(
          this.externalConfigurations().map(
            (config) => config.externalFilter$ as Observable<string[]>
          )
        )
          .pipe(
            distinctUntilChanged(
              (a, b) => JSON.stringify(a) === JSON.stringify(b)
            ),
            debounceTime(this.debounceTime),
            tap((data) => {
              untracked(() => {
                this.onFiltersChanged(
                  this.externalConfigurations().map((config, index) => ({
                    key: config.key as string,
                    value: (data as any)[index],
                  }))
                )
              })
            }),
            untilDestroyed(this)
          )
          .subscribe()
      },
      { injector: this._injector }
    )
    if (this.emitOnInit() && this.externalConfigurations()?.length === 0) {
      this.filterChanged.emit(this.currentFilterValues())
    }
  }
}
